import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  CommonList,
  ConfirmSelectionButton,
  ListSelectionMode,
  SelectionPageHeader,
} from '../../common/components';
import type { Stock } from '../../../types/stock';
import type { StockSelectionContext } from '../types';
import { useStockData } from '../useStockData';
import { useStockSingleSelection } from './useStockSingleSelection';

export default function StockSingleSelectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = id ? parseInt(id) : null;

  const selectionContext = location.state as StockSelectionContext;

  const { data } = useStockData({ companyId });
  const { selectedStock, handleSelectStock } = useStockSingleSelection();

  const handleConfirmSelection = () => {
    if (selectedStock && selectionContext?.returnPath) {
      const returnStateKey = selectionContext?.returnStateKey || 'selectedStock';
      navigate(selectionContext.returnPath, {
        state: {
          [returnStateKey]: selectedStock,
          activityFeId: selectionContext?.activityFeId,
        },
      });
    }
  };

  const handleDoubleClickStock = (stock: Stock) => {
    if (selectionContext?.returnPath) {
      const returnStateKey = selectionContext?.returnStateKey || 'selectedStock';
      navigate(selectionContext.returnPath, {
        state: {
          [returnStateKey]: stock,
          activityFeId: selectionContext?.activityFeId,
        },
      });
    }
  };

  if (data.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {data.error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <SelectionPageHeader
        title="Вибір складу"
        companyId={companyId}
        returnPath={selectionContext?.returnPath}
      >
        {selectedStock && <ConfirmSelectionButton onClick={handleConfirmSelection} />}
      </SelectionPageHeader>

      {/* Content */}
      {data.stocks.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: 'background.default',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" gutterBottom>
            Складів ще немає
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Немає складів для вибору. Поверніться і створіть склад спочатку.
          </Typography>
        </Paper>
      ) : (
        <CommonList<Stock>
          items={data.stocks}
          toListItem={(stock) => ({ id: stock.id, primary: stock.name })}
          selectionMode={ListSelectionMode.SINGLE_SELECTION}
          selectedItemId={selectedStock?.id || selectionContext?.currentStockId}
          onSelect={handleSelectStock}
          onDoubleClick={handleDoubleClickStock}
          emptyMessage="Немає складів для вибору"
        />
      )}
    </Box>
  );
}
